type TransferItemLike = {
  kind?: string;
  getAsFile?: () => File | null;
};

type PromptAttachmentLike = {
  name: string;
  size: number;
  mimeType: string;
  kind: string;
  data?: string | null;
};

type TransferLike = {
  items?: ArrayLike<TransferItemLike> | null;
  files?: ArrayLike<File> | null;
};

function fileSignature(file: File): string {
  return [file.name, file.size, file.type, file.lastModified].join(":");
}

function promptAttachmentSignature(attachment: PromptAttachmentLike): string {
  return JSON.stringify([
    attachment.name,
    attachment.size,
    attachment.mimeType,
    attachment.kind,
    attachment.data || "",
  ]);
}

export function extractFilesFromTransfer(transfer?: TransferLike | null): File[] {
  if (!transfer) return [];

  const files: File[] = [];
  const seen = new Set<string>();

  const addFile = (file: File | null | undefined) => {
    if (!file) return;
    const signature = fileSignature(file);
    if (seen.has(signature)) return;
    seen.add(signature);
    files.push(file);
  };

  if (transfer.items) {
    for (const item of Array.from(transfer.items)) {
      if (!item || item.kind !== "file") continue;
      addFile(item.getAsFile?.());
    }
  }

  if (transfer.files) {
    for (const file of Array.from(transfer.files)) {
      addFile(file);
    }
  }

  return files;
}

export function mergePromptAttachments<T extends PromptAttachmentLike>(
  current: T[],
  incoming: T[],
): T[] {
  if (incoming.length === 0) return current;

  const merged = [...current];
  const seen = new Set(current.map((attachment) => promptAttachmentSignature(attachment)));

  for (const attachment of incoming) {
    const signature = promptAttachmentSignature(attachment);
    if (seen.has(signature)) continue;
    seen.add(signature);
    merged.push(attachment);
  }

  return merged;
}
